import { useCallback, useEffect, useState } from "react";
import {
  ClassError,
  fetchClassGames,
  fetchObjectives,
  setGameOpen,
  type Catalogue,
  type ClassGames,
} from "../class-api.js";
import { COPY, fill, strandLabel, taskLabel, taskPurpose } from "../copy.js";
import { fa } from "../format.js";
import { Creature } from "../creature.js";
import { familyLook } from "../accent.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * Which games this class may play (ADR-014).
 *
 * **Everything is shut until a teacher opens it**, which is the opposite of what a shelf of games
 * usually does and is the point: a teacher who has spent a week on counting does not want half the
 * room in the maze, and a four-year-old handed sixteen tiles picks the loudest one every time.
 *
 * One switch per game, for the whole class — never per child. A teacher with two groups can teach them
 * different things; giving one child a game their neighbour cannot see is a difference visible across
 * a table, and nobody asked for it.
 *
 * The row is the switch. A separate control beside a row that also looks tappable is two targets for
 * one decision, and on a phone the wrong one gets hit.
 *
 * **Grouped by the area of the week each game belongs to**, which is the change that made the screen
 * answerable. It was one flat list of twenty-five rows, each described by a line written for a
 * five-year-old — so a teacher could see what a child DOES in a game and never what it is for, and
 * the question they actually arrive with ("we are on counting this week; which of these help") had
 * no answer short of opening each one.
 *
 * The grouping is the SAME seven words the assign screen and the class grid already use, and it is
 * derived from the objectives rather than written down again: every game carries objectives, every
 * objective names a strand, and today no game spans two. A fourth taxonomy invented for this screen
 * would be a fourth thing to keep true.
 */

const CARD = "rounded-lg bg-card shadow-card";

type State =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; games: ClassGames; catalogue: Catalogue | null };

/**
 * One game as a teacher decides about it — which is a FAMILY, not a task.
 *
 * **The screen listed tasks, and that was a bug rather than a miscount.** Forward span ships as two
 * arms of the same game (A-13): the same measurement, one bare and one wrapped in a story. A child
 * is given exactly one of them, hashed from their profile id, and the shelf checks that ONE id
 * against what the class has open — so a teacher who opened the bare arm locked the game for every
 * child hashed to the story arm, having just been told they had opened it.
 *
 * A row is a family now and its switch sets every task in it. The teacher's unit of decision is
 * "the firefly game", never "which arm of an experiment nobody has finished".
 */
interface Game {
  family: string;
  /** Every task in the family. All of them move together. */
  taskIds: string[];
  /** The task whose name and creature stand for the family: the first the bundle lists. */
  taskId: string;
  /** Open only when every arm is, so a half-open family reads as shut and one tap repairs it. */
  open: boolean;
  /** Distinct objectives across the family — counted once even when two arms both carry them. */
  goals: number;
}

/** One area of the week, with the games that teach it. */
interface Group {
  strand: string;
  games: Game[];
}

/**
 * The games, grouped by the strand their objectives belong to.
 *
 * A game with no objective — which nothing in the bundle is — would have nowhere to go, so it falls
 * into a group of its own at the end rather than disappearing off the screen. Losing a game from
 * the only place it can be switched on is worse than an odd-looking heading.
 */
function groupByStrand(games: ClassGames, catalogue: Catalogue | null): Group[] {
  const strandOf = new Map<string, string>();
  const objectivesOf = new Map<string, Set<string>>();
  const order: string[] = [];
  for (const objective of catalogue?.objectives ?? []) {
    if (!order.includes(objective.strand)) order.push(objective.strand);
    for (const step of objective.steps) {
      if (!strandOf.has(step.taskId)) strandOf.set(step.taskId, objective.strand);
      const seen = objectivesOf.get(step.taskId) ?? new Set<string>();
      seen.add(objective.id);
      objectivesOf.set(step.taskId, seen);
    }
  }

  // Tasks collapse into families first, so the two arms of forward span are one row.
  const byFamily: Game[] = [];
  for (const entry of games) {
    const found = byFamily.find((g) => g.family === entry.family);
    if (found) {
      found.taskIds.push(entry.taskId);
      // Alphabetically first, which is the arm `oneTaskPerFamily` pins a teacher to. Picking a
      // different one here is how the games list and the preview came to call one game two names.
      found.taskIds.sort((a, b) => a.localeCompare(b));
      found.taskId = found.taskIds[0] as string;
      found.open = found.open && entry.open;
    } else {
      byFamily.push({
        family: entry.family,
        taskIds: [entry.taskId],
        taskId: entry.taskId,
        open: entry.open,
        goals: 0,
      });
    }
  }
  for (const game of byFamily) {
    const distinct = new Set<string>();
    for (const taskId of game.taskIds) {
      for (const id of objectivesOf.get(taskId) ?? []) distinct.add(id);
    }
    game.goals = distinct.size;
  }

  const groups: Group[] = [];
  for (const game of byFamily) {
    const strand = strandOf.get(game.taskId) ?? "";
    const found = groups.find((g) => g.strand === strand);
    if (found) found.games.push(game);
    else groups.push({ strand, games: [game] });
  }
  // Catalogue order is teaching order, and a strand with no home goes last.
  groups.sort((a, b) => {
    const ai = order.indexOf(a.strand);
    const bi = order.indexOf(b.strand);
    return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
  });
  return groups;
}

export function GamesScreen({
  onBack,
  onSignedOut,
}: {
  onBack: () => void;
  onSignedOut: () => void;
}) {
  const { current } = useClasses();
  const classId = current?.classId ?? null;
  const [state, setState] = useState<State>({ kind: "loading" });
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!classId) return;
    try {
      /**
       * The catalogue rides along, and its failure is not the list's.
       *
       * It carries the strand each game belongs to, and without it the screen falls back to one
       * ungrouped list — which is what it used to be, and still lets a teacher open every game.
       * A grouping that fails should cost the headings, not the switches.
       */
      const [games, catalogue] = await Promise.all([
        fetchClassGames(classId),
        fetchObjectives().catch(() => null),
      ]);
      setState({ kind: "ready", games, catalogue });
    } catch (err) {
      if (err instanceof ClassError && err.failure === "unauthenticated") {
        onSignedOut();
        return;
      }
      setState({
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [classId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  const framed = (body: React.ReactNode) => (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      <ScreenHeader
        title={COPY.teacher.games.title}
        subtitle={COPY.teacher.games.intro}
        onBack={onBack}
        backLabel={COPY.teacher.games.back}
      />
      {body}
    </div>
  );

  if (state.kind === "loading") {
    return framed(<p className="p-lg text-center text-muted">{COPY.teacher.loading}</p>);
  }
  if (state.kind === "failed") {
    return framed(
      <p className="p-lg text-center text-muted">
        {fill(COPY.teacher.failed, { message: state.message })}
      </p>,
    );
  }

  const { games } = state;
  const groups = groupByStrand(games, state.catalogue);
  // Counted in families, because that is what a teacher opened and what a child will see.
  const openCount = groups.reduce((n, g) => n + g.games.filter((x) => x.open).length, 0);

  /**
   * The switch flips in place, then the server is asked.
   *
   * Optimistic because the answer is a boolean the client already knows and the round trip is the only
   * thing between a tap and the change — a switch that waits reads as broken. A refusal re-reads the
   * whole list, so the screen ends up showing what the server says either way.
   */
  /**
   * One game, which is every arm of it.
   *
   * Both arms of forward span move together, because a child is given one of them and the shelf
   * checks that one — opening half a family is opening it for half a class, silently.
   */
  const setOpen = async (taskIds: readonly string[], open: boolean, key: string) => {
    if (!classId) return;
    const wanted = games.filter((g) => taskIds.includes(g.taskId) && g.open !== open);
    if (wanted.length === 0) return;
    setBusy(key);
    setState({
      kind: "ready",
      games: games.map((g) => (taskIds.includes(g.taskId) ? { ...g, open } : g)),
      catalogue: state.catalogue,
    });
    try {
      for (const entry of wanted) await setGameOpen(classId, entry.taskId, open);
    } finally {
      await load();
      setBusy(null);
    }
  };

  /**
   * A whole area of the week, in one tap.
   *
   * The actual unit of a teacher's decision: they are on counting this week, so counting goes on.
   * Sent one game at a time because that is the endpoint there is, and reloaded once at the end
   * rather than after each — a list that redraws six times reads as a stutter.
   */
  const toggleGroup = (group: Group, open: boolean) =>
    setOpen(group.games.flatMap((g) => g.taskIds), open, group.strand);

  return framed(
    <>
      {/*
        * One fact, not two.
        *
        * This line briefly carried the count AND a note about assignments opening games, run together
        * into a single paragraph — two unrelated sentences in one banner, which reads as a mistake.
        * The assignment behaviour is said on the goals screen, where the assigning happens.
        */}
      <p className="mb-md px-1 text-caption text-muted">
        {openCount === 0
          ? COPY.teacher.games.noneOpen
          : fill(COPY.teacher.games.openCount, { count: fa(openCount) })}
      </p>

      {groups.map((group) => {
        const openHere = group.games.filter((g) => g.open).length;
        const allOpen = openHere === group.games.length;
        return (
          <section key={group.strand || "other"} className="mb-lg">
            <h2 className="mb-sm flex flex-wrap items-baseline justify-between gap-x-md gap-y-1 px-1">
              <span className="text-heading">
                {group.strand ? strandLabel(group.strand) : COPY.teacher.games.title}
              </span>
              <span className="flex shrink-0 items-baseline gap-md">
                <span className="text-caption text-muted tabular-nums">
                  {fill(COPY.teacher.games.strandOpenSome, {
                    open: fa(openHere),
                    total: fa(group.games.length),
                  })}
                </span>
                <button
                  className="text-caption text-grape disabled:opacity-60"
                  disabled={busy === group.strand}
                  onClick={() => void toggleGroup(group, !allOpen)}
                >
                  {allOpen
                    ? COPY.teacher.games.strandCloseAll
                    : COPY.teacher.games.strandOpenAll}
                </button>
              </span>
            </h2>

            <ul className={`${CARD} overflow-hidden`}>
              {group.games.map((game) => {
                const look = familyLook(game.family);
                const goals = game.goals;
                return (
                  <li key={game.family} className="border-b border-edge last:border-b-0">
                    <button
                      onClick={() => void setOpen(game.taskIds, !game.open, game.family)}
                      disabled={busy === game.family}
                      aria-pressed={game.open}
                      className="flex w-full items-center gap-sm px-md py-md text-start disabled:opacity-60"
                    >
                      {/* The game's own creature, so the list reads as the shelf a child will see. */}
                      <span className={`shrink-0 ${game.open ? "" : "opacity-40 grayscale"}`}>
                        <Creature kind={look.creature} accent={look.accent} size={38} />
                      </span>

                      {/*
                        * Name and goal count on one line, purpose under it.
                        *
                        * The row used to stack three lines of text in a column squeezed between a
                        * creature and a switch wide enough to carry a word, so on a phone the middle
                        * of every row was about a hundred and seventy pixels and everything wrapped
                        * into everything. The count moved up beside the name, which it belongs with
                        * anyway — both describe the game rather than what it teaches — and that is
                        * one line back per row.
                        */}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-sm">
                          <span className="min-w-0 flex-1 truncate text-body">
                            {taskLabel(game.taskId)}
                          </span>
                          {goals > 0 && (
                            <span className="shrink-0 text-caption text-muted tabular-nums">
                              {fill(COPY.teacher.games.goalCount, { count: fa(goals) })}
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-caption text-muted">
                          {taskPurpose(game.taskId)}
                        </span>
                      </span>

                      <Switch on={game.open} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </>,
  );
}

/**
 * A drawn switch rather than a checkbox.
 *
 * Two states with a word beside each, because a bare toggle in an RTL layout has no agreed direction
 * for "on" — the knob slides one way here and the other way in a Latin interface, and the label is
 * what makes it unambiguous either way.
 */
function Switch({ on }: { on: boolean }) {
  return (
    /*
     * The word sits UNDER the knob rather than beside it.
     *
     * Side by side, the pair was about ninety pixels of a three-hundred-pixel row, and every one of
     * those pixels came out of the column the game's own words live in. Stacked it is under fifty
     * and the label is still there — which it has to be, for the reason below.
     */
    <span className="flex shrink-0 flex-col items-center gap-1">
      <span
        className={`flex h-7 w-12 items-center rounded-pill p-1 transition-colors ${
          on ? "justify-end bg-mint" : "justify-start bg-edge"
        }`}
        aria-hidden
      >
        <span className="size-5 rounded-pill bg-card shadow-card" />
      </span>
      <span className={`text-caption ${on ? "text-ink" : "text-muted"}`}>
        {on ? COPY.teacher.games.open : COPY.teacher.games.closed}
      </span>
    </span>
  );
}
